// src/components/RecommendationModal.jsx
import React, { useState } from 'react';
import { amortizedTotal } from '../calculators/chapter13Calculator';
import {
  CheckCircleIcon,
  AcademicCapIcon,
  DocumentDuplicateIcon,
  HomeIcon,
  TruckIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowPathIcon,
  RocketLaunchIcon,
} from '@heroicons/react/20/solid';
export default function RecommendationModal({
  isOpen,
  onClose,
  recommendationResult,
  answers,
}) {
  const [lowCostCh13Selected, setLowCostCh13Selected] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [selectedCh13Plan, setSelectedCh13Plan] = useState(36);
  const { recommendation, fees = {}, computedValues = {} } = recommendationResult;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isSwitchingChapter, setIsSwitchingChapter] = useState(false);
  // Build a caseId string matching the backend’s generate_case_id
  function buildCaseId(data) {
    const slugify = s => s.toLowerCase().replace(/\s+/g, '-');
    let parts = [ slugify(data.lastName), slugify(data.firstName) ];
    if (data.middleName) parts.push(slugify(data.middleName));
    let namePart = parts.join('-');
  
    if (data.spouseFiling?.toLowerCase() === 'yes') {
      const sp = [
        slugify(data.spouseLastName),
        slugify(data.spouseFirstName),
        slugify(data.spouseMiddleName),

      ].filter(Boolean).join('-');
      namePart = `${namePart}&${sp}`;
    }
  
    return `${namePart}-${data.dob}`;  // dob already in YYYY-MM-DD
  }
  const caseId = buildCaseId(answers);
  const switchChapter = async (chapter) => {
    const url = `${process.env.REACT_APP_API_URL}/case/${caseId}/switch-chapter-intake/`;
    const resp = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ chapter }),
    });
    if (!resp.ok) {
      const { error } = await resp.json().catch(() => ({}));
      throw new Error(error || resp.statusText);
    }
    return resp.json();
  };
  const acceptDisclaimer = async () => {
    try {
        setIsSwitchingChapter(true);
      await switchChapter('13');
      setIsSwitchingChapter(false)
      setLowCostCh13Selected(true);
      setShowDisclaimerModal(false);
          // or refresh UI however you like
    } catch (e) {
        setIsSwitchingChapter(false);
      alert('Could not switch to Chapter 13:\n' + e.message);
    }
  };
  // when they toggle the checkbox
  const handleLowCostToggle = async (checked) => {
    if (checked) {
      // show disclaimer before actually switching
      setShowDisclaimerModal(true);
    } else {
      try {
        setIsSwitchingChapter(true);
        await switchChapter('7');
        setLowCostCh13Selected(false);
      } catch (e) {
        alert('Could not switch back to Chapter 7:\n' + e.message);
      } finally {
        setIsSwitchingChapter(false);
      }
    }
  };
  // sum medical debts by parsing amountOwed
  const primaryMedicalDebt = Math.round((answers.medicalDebtDetails || []).reduce(
    (sum, d) => sum + parseFloat(d.amountOwed || 0),
    0
  ));
  const spouseMedicalDebt = Math.round((answers.spouseMedicalDebtDetails || []).reduce(
    (sum, d) => sum + parseFloat(d.amountOwed || 0),
    0
  ));
  const medicalDebt = primaryMedicalDebt + spouseMedicalDebt
  // helper to compute total payments over 3 years at 25% APR
  function totalAmortized(amount) {
    const r = 0.25 / 12;
    const n = 36;
    const m = amount * (r / (1 - (1 + r) ** -n));
    return m * n;
  }

  // sum and amortize personal loans
  const personalLoanTotal = (answers.personalLoanDebtDetails || []).reduce(
    (sum, d) => sum + parseFloat(d.debtBalance || 0),
    0
  );
  const primaryPersonalLoanPayment = Math.round(totalAmortized(personalLoanTotal));

  const spousePersonalLoanTotal = (answers.spousePersonalLoanDebtDetails || []).reduce(
    (sum, d) => sum + parseFloat(d.debtBalance || 0),
    0
  );
  const spousePersonalLoanPayment = Math.round(totalAmortized(spousePersonalLoanTotal));

const personalLoanPayment = primaryPersonalLoanPayment + spousePersonalLoanPayment
  // sum and amortize credit card debts
  const creditCardTotal = (answers.creditCardDebtDetails || []).reduce(
    (sum, d) => sum + parseFloat(d.debtBalance || 0),
    0
  );
  const primaryCreditCardPayment = Math.round(totalAmortized(creditCardTotal));

  const spouseCreditCardTotal = (answers.spouseCreditCardDebtDetails || []).reduce(
    (sum, d) => sum + parseFloat(d.debtBalance || 0),
    0
  );
  const spouseCreditCardPayment = Math.round(totalAmortized(spouseCreditCardTotal));

const creditCardPayment = primaryCreditCardPayment + spouseCreditCardPayment
  // sum other debts by parsing amountOwed
  const primaryOtherDebt = Math.round((answers.otherDebtDetails || []).reduce(
    (sum, d) => sum + parseFloat(d.amountOwed || 0),
    0
  ));

  const spouseOtherDebt = Math.round((answers.spouseOtherDebtDetails || []).reduce(
    (sum, d) => sum + parseFloat(d.amountOwed || 0),
    0
  ));

  const otherDebt = primaryOtherDebt + spouseOtherDebt

  // total cost not to file
  const costNotToFile =
    medicalDebt +
    personalLoanPayment +
    creditCardPayment +
    otherDebt

    const totalSavings = costNotToFile - (fees.total || 0);
  if (!isOpen) return null;

  // Build slide decks for each case
  const slidesByRec = {
    'Chapter 7 recommended.': [
      {
        icon: CheckCircleIcon,
        title: 'Recommended: Chapter 7',
        content: (
          <div className="space-y-4 text-gray-700">
            <p>
              Chapter 7 is your federal right to debt relief under the Bankruptcy provision of the U.S. Code. Based on your answers you qualify!
            </p>
            <p>
              Let Cisowski Law guide you over the next few slides for a rundown of the legal requirements and common questions for your fresh start.
            </p>
          </div>
        ),
      },
      {
        icon: AcademicCapIcon,
        title: 'What is Chapter 7?',
        content: (
          <div className="space-y-4 text-gray-700">
            <p>
              Your right under the Bankruptcy Code - Chapter 7 is a right to a fresh start free from debt. A "discharge" will be granted eliminating your liability on most debts.
            </p>
            <p>
              Since you qualify, you can take advantage by filing a Chapter 7 Petition with the court.
            </p>
          </div>
        ),
      },
      {
        icon: DocumentDuplicateIcon,
        title: 'How do I file?',
        content: (
          <div className="space-y-4 text-gray-700">
            <p>
              Petitioning for relief is the process of submitting the required schedules and statements
              with the federal court in your filing district.
            </p>
            <p>
              Your creditors are immediately notified upon filing and must stop ALL collection
              attempts, including:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1 text-gray-700">
              <li>Phone calls</li>
              <li>Lawsuits</li>
              <li>Garnishments</li>
            </ul>
          </div>
        ),
      },
      answers.ownsHome === 'yes' && {
        icon: HomeIcon,
        title: 'Can I keep my home?',
        content: (
          <div className="space-y-4 text-gray-700">
            <p>
              Yes! We have performed the liquidation analysis based on answers to the questionnaire.
            </p>
            <p>
              The policy of providing a "fresh start" means you are entitled to your home and property (within reason).
            </p>
            <p>
              Make sure you stay current on your mortgage though, this debt will go through Ch 7.
            </p>
          </div>
        ),
      },
      answers.hasVehicle === 'yes' && {
        icon: TruckIcon,
        title: 'Can I keep my vehicles?',
        content: (
          <div className="space-y-4 text-gray-700">
            <p>
              Yes! To further the goal of providing a fresh start, you are entitled to your vehicles (within reason).
            </p>
            <p>
              Car loans survive your Ch 7 though, so stay current on those payments if you want to keep the property.
            </p>
          </div>
        ),
      },
      {
        icon: CurrencyDollarIcon,
        title: 'What It Costs to file',
        content: (
          <div className="space-y-4 text-gray-700">
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>
                Attorney fees: ${fees.attorneyFee?.toLocaleString()} ($500 to start)
              </li>
              <li>
                Court cost: ${fees.filingFee} - due 4 months after filing
              </li>
              <li>
                Credit counseling: ${fees.creditCounseling}
              </li>
              <li>
                <strong>Total cost: ${fees.total?.toLocaleString()}</strong>
              </li>
            </ul>
            <p>
              Looking for lower upfront cost? File Ch 13 for $100 to start + $145/mo for 36 months.
            </p>
          </div>
        ),
      },
      {
        icon: ArrowTrendingUpIcon,
        title: 'Cost Not to File',
        content: [
          medicalDebt > 0 && `Medical debt: $${medicalDebt.toLocaleString()}`,
          personalLoanPayment > 0 && `Personal loans: $${personalLoanPayment.toLocaleString()}*`,
          creditCardPayment > 0 && `Credit cards*: $${creditCardPayment.toLocaleString()}*`,
          otherDebt > 0 && `Other debt: $${otherDebt.toLocaleString()}`,
          `Total without filing: $${costNotToFile.toLocaleString()}`,
          `*Personal loan and credit card amounts assume 25% APR amortized over 3 years`,
        ].filter(Boolean).join('\n'),
      },
      {
        icon: RocketLaunchIcon,
        title: 'Ready to Start?',
        content: (
            <div className="space-y-4 text-gray-700">
              <p>
                We have created your case, and sent you a welcome email! Click below to
                login to your case dashboard and view the next steps to Bankruptcy relief.
              </p>
              <div className="flex items-center space-x-2">
              {isSwitchingChapter ? (
                  <ArrowPathIcon className="h-5 w-5 text-indigo-600 animate-spin" />
                ) : (
                  <input
                    type="checkbox"
                    id="lowCostCh13Option"
                    checked={lowCostCh13Selected}
                    disabled={isSwitchingChapter}
                    onChange={e => handleLowCostToggle(e.target.checked)}
                    className="form-checkbox h-5 w-5 text-indigo-600"
                  />
                )}
                <label htmlFor="lowCostCh13Option" className="text-gray-800">
                  Interested in a low-cost Chapter 13 plan? ($100 upfront fee + $145/mo for 3 years)
                </label>
              </div>
            </div>
          ),
      },
    ].filter(Boolean),

    'Chapter 13 recommended.': [
      {
        icon: CheckCircleIcon,
        title: 'Recommended: Chapter 13',
        content: (
          <div className="space-y-4 text-gray-700">
            <p>
            Chapter 13 is a repayment plan under the Bankruptcy provision of the U.S. Code that allows you to pay back
              your debts over 3–5 years while keeping your property. The payment is fixed by a set of rules that typically results in a manageable monthly payment.
            </p>
            <p>
              Let Cisowski Law guide you over the next few slides for a rundown of the legal requirements and common questions for your fresh start.
            </p>
          </div>
        ),
      },
      {
        icon: AcademicCapIcon,
        title: 'What is Chapter 13 Plan?',
        content: (
          <div className="space-y-4 text-gray-700">
            <p>
              Your Chapter 13 plan is a court-approved repayment of your debts with some benefits and some drawbacks.
            </p>
            <h4 className="font-semibold">Pros</h4>
            <ul className="list-disc list-inside ml-5 space-y-1">
              <li>Amounts owed freeze at filing and additional interest and finance charges no longer accrue</li>
              <li>If qualified, you can "cramdown" payments on car loans to get a better interest rate or monthly payment</li>
              <li>You may only have to pay a very small portion of your unsecured debts (as low as 1%)</li>
            </ul>
            <h4 className="font-semibold">Cons</h4>
            <ul className="list-disc list-inside ml-5 space-y-1">
              <li>While in the plan, NO NEW DEBT unless approved by the court</li>
              <li>Your tax refunds are subject to the plan and you can only keep up to ${computedValues.plan36.refundAllowed}</li>
              <li>If you choose a 36 month plan, ALL tax refunds must be paid into the plan</li>
            </ul>
          </div>
        ),
      },
      {
        icon: CurrencyDollarIcon,
        title: 'Your Chapter 13 Plan',
        content: (
          <div className="space-y-6 text-gray-700 px-4 sm:px-6">
            {/* Plan summary header */}
            {(() => {
              const planData = recommendationResult.computedValues[`plan${selectedCh13Plan}`];
              // Destructure monthlyPayment, mortgageItems, vehicleItems
              const { monthlyPayment, mortgageItems, vehicleItems } = planData;
              // Compute excluded secured total and names
              const excludedTotal =
                mortgageItems
                  .filter(i => !i.included)
                  .reduce((sum, i) => sum + Number(i.monthlyPayment) + (i.totalArrearage / selectedCh13Plan), 0)
                +
                vehicleItems
                  .filter(i => !i.included)
                  .reduce((sum, i) => sum + Number(i.monthlyPayment) + (i.totalArrearage / selectedCh13Plan), 0);
              const excludedNames = [
                ...mortgageItems.filter(i => !i.included).map(i => i.creditor + ' Mortgage'),
                ...vehicleItems.filter(i => !i.included).map(i => i.description + ' Loan'),
              ];
              return (
                <div className="px-4 sm:px-6">
                  <p className="text-gray-700">
                    Here’s your Chapter 13 plan payment and breakdown:
                  </p>
                  <p className="text-3xl sm:text-4xl font-bold text-indigo-600 mt-2 mb-4">
                    ${monthlyPayment.toLocaleString()}<span className="text-base font-medium text-gray-600"> / month</span>
                  </p>
                  <p className="text-xs text-gray-600">
                    + ${Math.round(excludedTotal).toLocaleString()} paid directly to secured creditors outside of the plan:
                    {excludedNames.length > 0 ? (
                      <span className="font-medium"> {excludedNames.join(', ')}</span>
                    ) : (
                      <span className="italic"> None</span>
                    )}
                  </p>
                </div>
              );
            })()}

            {/* Plan toggle */}
            <div className="flex space-x-4">
              <button
                className={`px-3 py-1 rounded ${selectedCh13Plan === 36 ? 'bg-indigo-600 text-white' : 'bg-gray-200'}`}
                onClick={() => setSelectedCh13Plan(36)}
              >
                36 months
              </button>
              <button
                className={`px-3 py-1 rounded ${selectedCh13Plan === 60 ? 'bg-indigo-600 text-white' : 'bg-gray-200'}`}
                onClick={() => setSelectedCh13Plan(60)}
              >
                60 months
              </button>
            </div>

            {/* Breakdown header */}
            <div className="mt-4 mb-2">
              <h4 className="text-lg font-semibold text-gray-800">
                Monthly Breakdown
              </h4>
            </div>

            {/* Simplified accordion only for secured line items */}
            <div className="space-y-2 text-sm sm:text-base px-0 sm:px-6">
              {(() => {
                const planData = recommendationResult.computedValues[`plan${selectedCh13Plan}`];
                const {
                  mortgageItems,
                  vehicleItems,
                  priorityItems,
                  unsecuredItems,
                  attorneyBalance,
                  trusteeFee,
                  monthlyPayment
                } = planData;
                const courtFee = priorityItems.filingFee;
                const {
                    unsecuredTotal,
                    unsecuredPlan,
                    totalLiquidationValue,
                    disposableIncomeResults,
                  } = planData;
                  const percent = unsecuredTotal > 0
                    ? (unsecuredPlan / unsecuredTotal)
                    : 0;
                const monthlyArrearage = i => i.totalArrearage / selectedCh13Plan;

                return (
                  <>
                    {/* Mortgage section */}
                    <details className="border rounded">
                      <summary className="px-4 py-2 font-semibold text-sm sm:text-base">
                        Mortgage Payments: $
                        {Math.round(
                          mortgageItems
                            .filter(i => i.included)
                            .reduce((sum, i) => sum + Number(i.monthlyPayment) + monthlyArrearage(i), 0)
                        ).toLocaleString()}
                      </summary>
                      <div className="divide-y divide-gray-200">
                        {mortgageItems
                          .filter(i => i.included)
                          .map((i, idx) => {
                            const amt = Math.round(Number(i.monthlyPayment) + (i.totalArrearage / selectedCh13Plan));
                            return (
                              <div
                                key={idx}
                                className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50"
                              >
                                <span>{i.creditor}</span>
                                <span>${amt.toLocaleString()}</span>
                              </div>
                            );
                          })}
                      </div>
                    </details>

                    {/* Vehicle section */}
                    <details className="border rounded">
                      <summary className="px-4 py-2 font-semibold text-sm sm:text-base">
                        Vehicle Payments: $
                        {Math.round(
                          vehicleItems
                            .filter(i => i.included)
                            .reduce((sum, i) => sum + Number(i.total/selectedCh13Plan), 0)
                        ).toLocaleString()}
                      </summary>
                      <div className="divide-y divide-gray-200">
                        {vehicleItems.filter(i => i.included).map((i, idx) => {
                          const amt = Math.round(Number(i.total / selectedCh13Plan));
                          return (
                            <div
                              key={idx}
                              className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50"
                            >
                              <span>{i.description}</span>
                              <span>${amt.toLocaleString()}</span>
                            </div>
                          );
                        })}
                      </div>
                    </details>

                    {/* Priority Debts section */}
                    <details className="border rounded">
                      <summary className="px-4 py-2 font-semibold text-sm sm:text-base">
                        Priority Debts: $
                        {Math.round(
                          (priorityItems.respondentTaxLast3 +
                            priorityItems.spouseTaxLast3 +
                            priorityItems.respondendentChildSupportArrears +
                            priorityItems.spouseChildSupportArrears) /
                            selectedCh13Plan
                        ).toLocaleString()}
                      </summary>
                      <div className="divide-y divide-gray-200">
                        {[
                          ['Recent Tax Debt', priorityItems.respondentTaxLast3 + priorityItems.spouseTaxLast3],
                          ['Child Support', priorityItems.respondendentChildSupportArrears + priorityItems.spouseChildSupportArrears],
                          ['Alimony', priorityItems.alimony || 0],
                        ].filter(([, total]) => total > 0).map(([label, total], idx) => (
                          <div
                            key={label}
                            className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50"
                          >
                            <span>{label}</span>
                            <span>${Math.round(total / selectedCh13Plan).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </details>

                    {/* Unsecured Debts section */}
                    <details className="border rounded">
                      <summary className="px-4 py-2 font-semibold text-sm sm:text-base">
                        Unsecured Debts: $
                        {Math.round(
                          ((unsecuredItems.creditCardTotal +
                            unsecuredItems.spouseCreditCardTotal +
                            unsecuredItems.personalLoanTotal +
                            unsecuredItems.spousePersonalLoanTotal +
                            unsecuredItems.medicalDebtTotal +
                            unsecuredItems.spouseMedicalDebtTotal +
                            unsecuredItems.nonPriorityTaxTotal +
                            unsecuredItems.spouseNonPriorityTaxTotal +
                            unsecuredItems.otherDebtTotal +
                            unsecuredItems.spouseOtherDebtTotal) /
                            selectedCh13Plan) * percent
                        ).toLocaleString()}
                      </summary>
                      <div className="divide-y divide-gray-200">
                        {[
                          ['Credit Cards', unsecuredItems.creditCardTotal * percent + unsecuredItems.spouseCreditCardTotal* percent],
                          ['Personal Loans', unsecuredItems.personalLoanTotal* percent + unsecuredItems.spousePersonalLoanTotal* percent],
                          ['Medical', unsecuredItems.medicalDebtTotal* percent + unsecuredItems.spouseMedicalDebtTotal* percent],
                          ['NonPriority Tax', unsecuredItems.nonPriorityTaxTotal* percent + unsecuredItems.spouseNonPriorityTaxTotal* percent],
                          ['Other', unsecuredItems.otherDebtTotal* percent + unsecuredItems.spouseOtherDebtTotal* percent],
                        ].map(([label, total], idx) => (
                          <div
                            key={label}
                            className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50"
                          >
                            <span>{label}</span>
                            <span>${Math.round(total / selectedCh13Plan).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </details>

                    {/* Admin Costs section */}
                    <details className="border rounded">
                      <summary className="px-4 py-2 font-semibold text-sm sm:text-base">
                        Admin Costs: $
                        {Math.round((attorneyBalance + courtFee + trusteeFee) / selectedCh13Plan).toLocaleString()}
                      </summary>
                      <div className="divide-y divide-gray-200">
                        {[
                          ['Attorney Fees', attorneyBalance],
                          ['Court Filing Fee', courtFee],
                          ['Trustee Fee', trusteeFee],
                        ].map(([label, total], idx) => (
                          <div
                            key={label}
                            className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50"
                          >
                            <span>{label}</span>
                            <span>${Math.round(total / selectedCh13Plan).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  </>
                );
              })()}
            </div>
          </div>
        ),
      },
      // --- NEW SLIDE: Secured Debts ---
      {
        icon: DocumentDuplicateIcon,
        title: 'Secured Debts',
        content: (() => {
          // pull in the plan data for the current selection
          const planData = recommendationResult.computedValues[`plan${selectedCh13Plan}`];
          const includedMortgages = planData.mortgageItems.filter(i => i.included);
          const excludedMortgages = planData.mortgageItems.filter(i => !i.included);
          const includedVehicles = planData.vehicleItems.filter(i => i.included);
          const excludedVehicles = planData.vehicleItems.filter(i => !i.included);

          return (
            <div className="space-y-4 text-gray-700 px-4 sm:px-6">
              <p>
                Certain secured debts—your mortgages and vehicle loans—are included in your Chapter 13 plan.
                You will no longer need to make separate payments on these included debts; they are paid through
                your plan payment.
              </p>

              {/* Included Debts */}
              <div>
                <h4 className="font-semibold mb-2">Secured Debts Included in Plan</h4>
                {includedMortgages.length + includedVehicles.length > 0 ? (
                  <div className="divide-y divide-gray-200">
                    {includedMortgages.map((m, idx) => (
                      <div
                        key={`inc-mort-${idx}`}
                        className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50"
                      >
                        <span>{m.creditor} Mortgage</span>
                        <span>Included</span>
                      </div>
                    ))}
                    {includedVehicles.map((v, idx) => (
                      <div
                        key={`inc-veh-${idx}`}
                        className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50"
                      >
                        <span>{v.description} Loan</span>
                        <span>Included</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="italic">None</p>
                )}
              </div>

              {/* Excluded Debts */}
              <div>
                <h4 className="font-semibold mb-2">Secured Debts Not Included in Plan*</h4>
                {excludedMortgages.length + excludedVehicles.length > 0 ? (
                  <div className="divide-y divide-gray-200">
                    {excludedMortgages.map((m, idx) => (
                      <div
                        key={`exc-mort-${idx}`}
                        className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50"
                      >
                        <span>{m.creditor} Mortgage</span>
                        <span>Continue Direct Payment</span>
                      </div>
                    ))}
                    {excludedVehicles.map((v, idx) => (
                      <div
                        key={`exc-veh-${idx}`}
                        className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50"
                      >
                        <span>{v.description} Vehicle Loan</span>
                        <span>Continue Direct Payment</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="italic">None</p>
                )}
              </div>
              <p className="mt-2 text-xs text-gray-500 italic">
                * We leave certain debts out of the plan to avoid trustee fees on each dollar paid into the plan.
              </p>
            </div>
          );
        })(),
      },
      // --- NEW SLIDE: Priority Debts ---
      {
        icon: CurrencyDollarIcon,
        title: 'Priority Debts',
        content: (() => {
          const planData = recommendationResult.computedValues[`plan${selectedCh13Plan}`];
          const { priorityItems } = planData;
          const debts = [
            ['Recent Tax Debt', priorityItems.respondentTaxLast3 + priorityItems.spouseTaxLast3],
            ['Child Support Arrears', priorityItems.respondendentChildSupportArrears + priorityItems.spouseChildSupportArrears],
          ].filter(([, total]) => total > 0);
          return (
            <div className="space-y-4 text-gray-700 px-4 sm:px-6">
              <p>
                Priority debts must be paid in full through your Chapter 13 plan. Below are your priority monthly obligations:
              </p>
              {debts.length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {debts.map(([label, total], idx) => (
                    <div
                      key={idx}
                      className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50"
                    >
                      <span>{label}</span>
                      <span>${Math.round(total / selectedCh13Plan).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="italic">None</p>
              )}
            </div>
          );
        })(),
      },
      // --- NEW SLIDE: Unsecured Debts Analysis ---
      {
        icon: DocumentDuplicateIcon,
        title: 'Unsecured Debts Analysis',
        content: (() => {
          const planData = recommendationResult.computedValues[`plan${selectedCh13Plan}`];
          const {
            unsecuredItems,
            unsecuredTotal,
            unsecuredPlan,
            totalLiquidationValue,
            disposableIncomeResults,
          } = planData;
          const percent = unsecuredTotal > 0
            ? Math.round((unsecuredPlan / unsecuredTotal) * 100)
            : 0;
          const dispInc = recommendationResult.computedValues.plan36.disposableIncomeResults.disposableIncome;
          return (
            <div className="space-y-4 text-gray-700 px-4 sm:px-6">
              <p>
                Your Chapter 13 plan pays back <strong>{percent}%</strong> of your unsecured debts (${unsecuredPlan.toLocaleString()} total) over {selectedCh13Plan} months.
              </p>
              <p>
                This percentage is set by two rules: the <strong>disposable income test</strong> determines how much you can afford based on your income and your average cost of living, and the <strong>best interest test</strong> determines what your creditors would receive if you liquidated all your assets.
              </p>
              <h4 className="font-semibold mb-2">Applicable Test(s):</h4>
              {[
                totalLiquidationValue > 0 && {
                  label: 'Best interest test',
                  message: `You must pay at least $${totalLiquidationValue.toLocaleString()} total to unsecured creditors up to 100%.`,
                },
                dispInc > 0 && {
                  label: 'Disposable income test',
                  message: `You must pay at least $${dispInc.toLocaleString()} per month up to 100%.`,
                }
              ]
                .filter(Boolean)
                .map((test, idx) => (
                  <p key={idx}>
                    <strong>{test.label}:</strong> {test.message}
                  </p>
                ))}
              {totalLiquidationValue <= 0 && dispInc <= 0 && (
                <p>
                  No minimum unsecured payment is required under either test.
                </p>
              )}
            </div>
          );
        })(),
      },
      {
        icon: DocumentDuplicateIcon,
        title: 'How to Pay the Plan',
        content: (
          <div className="space-y-4 text-gray-700 px-4 sm:px-6">
            <p>
              Your Chapter 13 plan payment is made as a single monthly payment to the Chapter 13 trustee. The trustee’s job is to distribute those funds to your creditors in accordance with the law and your confirmed plan.
            </p>
            <p>If you are employed, the preferred method is via a wage order sent to your employer. Some jurisdictions also allow:</p>
            <ul className="list-disc list-inside ml-4 space-y-1 text-sm sm:text-base">
              <li>ACH (electronic) payments</li>
              <li>Personal checks</li>
              <li>Cashier’s checks</li>
            </ul>
            <p className="mt-2 text-xs text-gray-500 italic">
              Your first plan payment is due within 30 days after your case is filed.
            </p>
          </div>
        ),
      },
      // Conditional slide only if they wouldn't qualify for Chapter 7
      (!recommendationResult.computedValues.dischargeEligible || recommendationResult.computedValues.liquidationInChapter7 || recommendationResult.computedValues.aboveMedianIncome || recommendationResult.computedValues.repoOrForeclosure) && (
        {
          icon: DocumentDuplicateIcon,
          title: 'Why Not Chapter 7?',
          content: (
            <div className="space-y-4 text-gray-700">
              {recommendationResult.computedValues.liquidationInChapter7 && (
                <p>Your home or other assets would be liquidated under Chapter 7 since you have substantial equity.</p>
              )}
              {recommendationResult.computedValues.aboveMedianIncome && (
                <p>Your income is above the median for your state and therefore do not qualify.</p>
              )}
              {!recommendationResult.computedValues.dischargeEligible && (
                <p>Your debts are not dischargeable under Chapter 7. You must wait 8 years between Ch 7 filings</p>
              )}
              {recommendationResult.computedValues.repoOrForeclosure && (
                <p>You are behind on vehicles or a home you intend to keep. Unless you can catch them up quick, a Ch 13 is best.</p>
              )}
            </div>
          ),
        }
      ),
      {
        icon: CurrencyDollarIcon,
        title: 'Upfront Costs to File',
        content: (
          <div className="space-y-4 text-gray-700 px-4 sm:px-6">
            <div className="divide-y divide-gray-200">
              <div className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50">
                <span className="font-bold text-indigo-700">Total Attorney Fee</span>
                <span>
                  ${fees.totalAttorneyFee?.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between p-2 text-sm sm:text-base">
                <span className="font-bold text-indigo-700">Required Before Filing</span>
                <span>${fees.attorneyFee?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50">
                <span className="font-bold text-indigo-700">Attorney Fee Through Plan</span>
                <span>
                  ${Math.max((fees.totalAttorneyFee || 0) - (fees.attorneyFee || 0), 0).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between p-2 text-sm sm:text-base">
                <span className="font-bold text-indigo-700">Credit Counseling Course</span>
                <span>
                  ${fees.creditCounseling?.toLocaleString()} (2 online courses @ $12 each)
                </span>
              </div>
            </div>
          </div>
        ),
      },
            // --- NEW SLIDE: Monthly Savings ---
            {
                icon: ArrowTrendingUpIcon,
                title: 'Monthly Savings',
                content: (() => {
                  const planData = recommendationResult.computedValues[`plan${selectedCh13Plan}`];
                  const {
                    monthlyPayment,
                    mortgageItems,
                    vehicleItems,
                    unsecuredItems,
                    priorityItems
                  } = planData;
        
                  // Secured debts paid outside plan
                  const excludedSecuredMonthly =
                    mortgageItems.filter(i => !i.included)
                      .reduce((sum, i) => sum + Number(i.monthlyPayment) + (i.totalArrearage / selectedCh13Plan), 0) +
                    vehicleItems.filter(i => !i.included)
                      .reduce((sum, i) => sum + Number(i.monthlyPayment) + (i.totalArrearage / selectedCh13Plan), 0);
        
                  // NEW: total secured monthly (all secured debts, included or excluded)
                  const totalSecuredMonthly =
                    mortgageItems.reduce((sum, i) => sum + Number(i.monthlyPayment) + (i.totalArrearage / selectedCh13Plan), 0) +
                    vehicleItems.reduce((sum, i) => sum + Number(i.monthlyPayment) + (i.totalArrearage / selectedCh13Plan), 0);
        
                  const planMonthlyCost = Math.round(monthlyPayment + excludedSecuredMonthly);
        
                  // Unsecured amortized portions
                  const creditCardMonthly = Math.round(
                    amortizedTotal(
                      unsecuredItems.creditCardTotal + unsecuredItems.spouseCreditCardTotal,
                      0.25,
                      selectedCh13Plan
                    ) / selectedCh13Plan
                  );
                  const personalLoanMonthly = Math.round(
                    amortizedTotal(
                      unsecuredItems.personalLoanTotal + unsecuredItems.spousePersonalLoanTotal,
                      0.25,
                      selectedCh13Plan
                    ) / selectedCh13Plan
                  );
                  const medicalMonthly = Math.round(
                    (unsecuredItems.medicalDebtTotal + unsecuredItems.spouseMedicalDebtTotal) /
                      selectedCh13Plan
                  );
                  const otherMonthly = Math.round(
                    (unsecuredItems.otherDebtTotal + unsecuredItems.spouseOtherDebtTotal) /
                      selectedCh13Plan
                  );
                  // Tax obligations amortized at 10%
                  const taxObligationTotal = priorityItems.respondentTaxLast3 + priorityItems.spouseTaxLast3;
                  const taxMonthly = Math.round(
                    amortizedTotal(taxObligationTotal, 0.10, selectedCh13Plan) / selectedCh13Plan
                  );
                  // Child support & alimony over plan
                  const supportTotal = 
                    priorityItems.respondendentChildSupportArrears +
                    priorityItems.spouseChildSupportArrears +
                    (priorityItems.alimony || 0);
                  const supportMonthly = Math.round(supportTotal / selectedCh13Plan);
        
                  // Use totalSecuredMonthly for current monthly cost
                  const currentMonthlyCost = Math.round(
                    totalSecuredMonthly +
                    creditCardMonthly +
                    personalLoanMonthly +
                    medicalMonthly +
                    otherMonthly +
                    taxMonthly +
                    supportMonthly
                  );
                  const savings = currentMonthlyCost - planMonthlyCost;
        
                  return (
                    <div className="space-y-4 text-gray-700 px-4 sm:px-6">
                      <p>Your estimated* current monthly debt payments:</p>
                      <div className="divide-y divide-gray-200">
                        {totalSecuredMonthly > 0 && (
                          <div className="flex justify-between p-2 text-sm sm:text-base">
                            <span>Total Secured Debts</span>
                            <span>${totalSecuredMonthly.toLocaleString()}</span>
                          </div>
                        )}
                        {creditCardMonthly > 0 && (
                          <div className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50">
                            <span>Credit Cards</span>
                            <span>${creditCardMonthly.toLocaleString()}</span>
                          </div>
                        )}
                        {personalLoanMonthly > 0 && (
                          <div className="flex justify-between p-2 text-sm sm:text-base">
                            <span>Personal Loans</span>
                            <span>${personalLoanMonthly.toLocaleString()}</span>
                          </div>
                        )}
                        {medicalMonthly > 0 && (
                          <div className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50">
                            <span>Medical Bills</span>
                            <span>${medicalMonthly.toLocaleString()}</span>
                          </div>
                        )}
                        {taxMonthly > 0 && (
                          <div className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50">
                            <span>Tax Obligations</span>
                            <span>${taxMonthly.toLocaleString()}</span>
                          </div>
                        )}
                        {otherMonthly > 0 && (
                          <div className="flex justify-between p-2 text-sm sm:text-base">
                            <span>Other Debts</span>
                            <span>${otherMonthly.toLocaleString()}</span>
                          </div>
                        )}
                        {supportMonthly > 0 && (
                          <div className="flex justify-between p-2 text-sm sm:text-base">
                            <span>Child Support & Alimony Arrears</span>
                            <span>${supportMonthly.toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 italic">
                        * Estimates based on payments spread over {selectedCh13Plan} months.
                      </p>
                      <p className="font-semibold mt-4">
                        Current Debt Payments: ${currentMonthlyCost.toLocaleString()}
                      </p>
                      <p className="font-semibold">
                        Chapter 13 Debt Payments: ${planMonthlyCost.toLocaleString()}
                      </p>
                      <p className="font-semibold text-green-600">
                        Estimated Monthly Savings: ${savings.toLocaleString()}
                      </p>
                      <p className="font-bold mt-2 text-green-600 underline">
                        Total Savings over {selectedCh13Plan} months: ${(savings * selectedCh13Plan).toLocaleString()}
                      </p>
                    </div>
                  );
                })(),
              },
              {
                icon: RocketLaunchIcon,
                title: 'Ready to Start?',
                content: (
                    <div className="space-y-4 text-gray-700">
                      <p>
                        We have created your case, and sent you a welcome email! Click below to
                        login to your case dashboard and view the next steps to Bankruptcy relief.
                      </p>
                      <div>
                        After you login, you will be able to:
                        <li>retain your attorney</li>
                        <li>obtain your credit report</li>
                        <li>upload required document</li>
                        <li>see your forms as they are completed</li>
                      </div>
                    </div>
                  ),
              },
    ].filter(Boolean),

    'Chapter 13 may not be an option based on your income level.': [
      {
        title: '🎉 You Qualify for Chapter 13!',
        text: `Chapter 13 is a repayment plan under the Bankruptcy Code. You qualify to reorganize your debts over 3–5 years. Cisowski Law will handle the paperwork.`
      },
      {
        title: '📑 How Ch 13 Works',
        text: `• You’ll pay back a portion or all of your debts over 3–5 years.  
• Payments usually cap at your disposable income.  
• You keep all your property.  
• No extra interest or penalties.`
      },
      {
        title: '📝 Your Plan',
        text: `(we’ll calculate your exact plan details once you file — coming soon!)`
      },
      {
        title: '⚠️ Why Not Chapter 7',
        text: recommendationResult.reason || `Your income exceeds the Chapter 7 means-test median.`
      },
      {
        title: '💲 What It Costs',
        text: `
• Attorney fees: $${fees.attorneyFee?.toLocaleString()}  
• Court filing fee: $${fees.filingFee}  
• Credit counseling: $${fees.creditCounseling}  
Total up front: $${fees.total?.toLocaleString()}`
      },
      {
        title: '🚀 Ready to Start?',
        text: `Click “Begin” to get your Chapter 13 plan filed with Cisowski Law.`
      }
    ],

    'Chapter 7 not recommended because your home (or other real estate) would be liquidated.': [
      {
        title: '⚠️ Recommendation',
        text: `Neither Chapter 7 nor Chapter 13 is recommended based on your profile. Please contact us for alternative solutions.`
      }
    ]
  };

  // pick the right deck
  const slides = slidesByRec[recommendation] || slidesByRec['Chapter 7 not recommended because your home (or other real estate) would be liquidated.'];
  const SlideIcon = slides[currentSlide].icon;
  const isLast = currentSlide === slides.length - 1;
  const isFirst = currentSlide === 0;

  return (
    
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      {/* Card container */}
      {showDisclaimerModal && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white rounded-lg shadow-lg max-w-sm w-full p-6">
      <h2 className="text-lg font-semibold mb-4">Disclaimer: Low-Cost Chapter 13 Plan</h2>
      <p className="mb-4 text-gray-700">
        Chapter 13 gives you the same creditor protections but requires you to commit to payments of
        $145 per month for 36 months, plus a $100 upfront fee. Additionally:
      </p>
      <li>Your payments will be deducted from your wages by court order to your employer</li>
      <li>You must give all tax refunds to the Plan</li>
      <li>You cannot acquire debt while in the Plan</li>
      <br />
      <div className="flex justify-end space-x-4">
        <button
          onClick={acceptDisclaimer}
          disabled={isSwitchingChapter}
          className={`bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 flex items-center justify-center ${isSwitchingChapter ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isSwitchingChapter && (
            <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
          )}
          Accept
        </button>
        <button
          onClick={() => {
            setLowCostCh13Selected(false);
            setShowDisclaimerModal(false);
          }}
          className="text-gray-600 hover:underline"
        >
          No Thanks
        </button>
      </div>
    </div>
  </div>
)}
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-auto my-4 overflow-auto max-h-[90vh] p-6">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 pb-4 mb-4">
          <div className="w-full flex items-center space-x-2 -mx-3 -mt-3 -my-3
            bg-gradient-to-r from-indigo-200 to-blue-20 bg-opacity-75
            text-gray-800
            rounded-2xl
            text-xl font-semibold
            px-6 py-3
            whitespace-nowrap">
            
            { SlideIcon && <SlideIcon className="w-6 h-6 text-indigo-600" /> }
            <h3 className="">
              {slides[currentSlide].title}
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="mb-6">
          {slides[currentSlide].title === 'Cost Not to File' ? (
            <div className="space-y-6 text-gray-700">
              {/* 1. Grouped debt breakdown */}
              <div className="text-lg font-medium">Debt Cost Breakdown</div>
              <ul className="list-disc list-inside space-y-1">
                {medicalDebt > 0 && <li>Medical debt: ${medicalDebt.toLocaleString()}</li>}
                {personalLoanPayment > 0 && <li>Personal loans*: ${personalLoanPayment.toLocaleString()}</li>}
                {creditCardPayment > 0 && <li>Credit cards*: ${creditCardPayment.toLocaleString()}</li>}
                {otherDebt > 0 && <li>Other debt: ${otherDebt.toLocaleString()}</li>}
              </ul>
              {/* 2. Totals */}
              <div className="space-y-1">
                <p className="font-semibold">Total cost if you don’t file: ${costNotToFile.toLocaleString()}</p>
                <p className="font-semibold">Estimated savings by filing: ${totalSavings.toLocaleString()}</p>
              </div>
              {/* 3. Asterisk footnote */}
              <p className="text-xs text-gray-500">
                *Personal loan and credit card amounts assume a 25% APR amortized over 3 years
              </p>
            </div>
          ) : (
            <div className="text-gray-700">
              {slides[currentSlide].content}
            </div>
          )}
        </div>

        {/* Footer / Navigation */}
        <div className="flex items-center justify-between border-t border-gray-200 pt-4">
          <button
            onClick={() => setCurrentSlide(i => Math.max(0, i - 1))}
            disabled={currentSlide === 0}
            className={`py-1 px-3 rounded ${currentSlide === 0 ? 'text-gray-400' : 'text-indigo-600 hover:underline'}`}
          >
            ← Back
          </button>

          <div className="text-sm text-gray-600">
            {currentSlide + 1} / {slides.length}
          </div>

          {currentSlide === slides.length - 1 ? (
            <button
              onClick={() => {
                const email = encodeURIComponent(answers.email || '');
                window.location.href = `https://app.cisowskilaw.com/login?email=${email}`;
              }}
              className="bg-indigo-600 text-white py-1 px-4 rounded hover:bg-indigo-700"
            >
              Login
            </button>
          ) : (
            <button
              onClick={() => setCurrentSlide(i => Math.min(slides.length - 1, i + 1))}
              className="bg-indigo-600 text-white py-1 px-4 rounded hover:bg-indigo-700"
            >
              Next →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
